import React, { useEffect, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { position, PositionProps, ColorProps, color } from "styled-system";
import css from "@styled-system/css";
import {
  Box,
  Typography,
  Button,
  Card,
  Banner,
  ResponsiveLayout,
} from "@bca/ui-library";
import { shallowEqual, useSelector } from "react-redux";

import { useWindowOffset } from "./useWindowOffset.hook";
import Vrm from "./Vrm";
import LotNumber from "./LotNumber";
import BinSolidSVG from "./Icons/BinSolidSVG";
import BinSVG from "./Icons/BinSVG";
import PrintMenuWithList from "./print/PrintMenuWithList";
import untrackVehicle from "./../api/untrackVehicle";
import trackVehicle from "../api/trackVehicle";
import addNote from "../api/addNote";
import deleteNote from "../api/deleteNote";

import { PrintSections } from "./print/PrintSection.type";
import { StoreType } from "../redux/StoreType";
import featureVehicle from "../api/featureVehicle";
import unfeatureVehicle from "../api/unfeatureVehicle";
import { GET_SOURCE_STATE } from "../redux/slices/sourceSlice";

const DEFAULT_BUTTON = {
  bgColor: "transparent",
  color: "primary-white",
  iconColor: "primary-white",
};

const ACTIVE_BUTTON = {
  bgColor: "transparent",
  color: "primary-white",
  iconColor: "secondary-orange",
};

interface VehicleInteractionProps {
  isOnSale?: boolean;
  vrm: string | string[];
  lotNumber: string;
  saleCode: string;
  description: string;
  lotNote: string;
  isTracked: boolean;
  setIsTracked: (value: boolean) => void;
  printSections: PrintSections[];
  isProvisional: boolean;
  isFeatured?: boolean;
  setIsFeatured: (value: boolean) => void;
  isAdmin?: boolean;
  isFeatureVehicle?: boolean;
  shouldRestrict?: boolean;
  inventoryId: string | null;
  isToUseAuthZero?: boolean;
  accessToken?: string;
}

interface DeleteModalProps {
  setShowOverlay: (value: boolean) => void;
  setShowNotesBox: (value: boolean) => void;
  setShowDeleteBox: (value: boolean) => void;
  setPreviousNotes: (value: string) => void;
  setNotes: (value: string) => void;
  setNoteButton: (value: any) => void;
  setHasDeleteNotesError: (value: boolean) => void;
  notes: string;
  setNoteOpen: (value: boolean) => void;
  vrm: string | string[];
  saleCode: string;
  inventoryId: string | null;
  isToUseAuthZero?: boolean;
  accessToken?: string;
}

interface DiscardModalProps {
  previousNotes: string | null;
  setShowOverlay: (value: boolean) => void;
  setShowDiscardBox: (value: boolean) => void;
  setShowNotesBox: (value: boolean) => void;
  setNotes: (value: string) => void;
  setNoteButton: (value: any) => void;
}

interface NoteCardProps {
  isPostSaleVDP: boolean;
  notes: string | null;
  previousNotes: string | null;
  setNotes: (value: string) => void;
  setPreviousNotes: (value: string) => void;
  setShowOverlay: (value: boolean) => void;
  setShowNotesBox: (value: boolean) => void;
  setShowDiscardBox: (value: boolean) => void;
  setShowDeleteBox: (value: boolean) => void;
  setNotesAnimationActive: (value: boolean) => void;
  setNoteButton: (value: any) => void;
  vrm: string | string[];
  setHasNotesError: (value: boolean) => void;
  setNoteOpen: (value: boolean) => void;
  lotNumber: string;
  saleCode: string;
  inventoryId: string | null;
  isToUseAuthZero?: boolean;
  accessToken?: string;
}

interface ErrorBuyerInteractionProps {
  closeBanner: () => void;
  hasNotesError: boolean;
  hasDeleteNotesError: boolean;
  hasFeatureError: boolean;
}

const VehicleDetailsWrapper = styled(Box)<PositionProps>`
  ${position}
`;

const iconAnimation = keyframes`
  0% {
    width: 16px;
    height: 16px;
  }

  50% {
    width: 20px;
    height: 20px;
  }

  100% {
    width: 16px;
    height: 16px;
  }
`;

const InteractionButton = styled(Button)<{
  css: string;
  isAnimationActive: boolean;
}>`
  transition: width 0.5s;
  ${(props) => props.css}
  &:not([disabled]):hover {
    cursor: pointer;
  }
  div {
    min-width: 16px;
    min-height: 16px;
    max-width: 20px;
    max-height: 20px;
    width: 16px;
    height: 16px;

    animation-name: ${(props) =>
      props.isAnimationActive ? iconAnimation : "none"};
    animation-duration: 0.5s;
  }
`;

const NotesButton = styled(Button)<ColorProps & { isAnimationActive: boolean }>`
  transition: width 0.5s;
  position: relative;
  z-index: 3;
  ${(props) => props.css}
  ${color}
  &:not([disabled]):hover {
    cursor: pointer;
  }
  div {
    min-width: 16px;
    min-height: 16px;
    max-width: 20px;
    max-height: 20px;
    width: 16px;
    height: 16px;

    animation-name: ${(props) =>
      props.isAnimationActive ? iconAnimation : "none"};
    animation-duration: 0.5s;
  }
`;

const TextWrapper = styled.p<{
  showFullWidth: boolean;
  children: React.ReactNode;
}>`
  height: 16px;
  width: ${(props) => (props.showFullWidth ? "90px" : "50px")};
  overflow: hidden;
  position: relative;
  transition: width 0.5s;

  span {
    margin: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 90px;
    text-align: ${(props) => (props.showFullWidth ? "center" : "left")};
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2;
  background-color: black;
  opacity: 0.7;
  width: 100vw;
  height: 100vh;
`;

const TextOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: black;
  opacity: 0.7;
  z-index: 2;
`;

const CloseButton = styled.button<{
  children: React.ReactNode;
  onClick: () => void;
}>`
  margin: 0;
  padding: 0;
  border: none;
  color: black;
  width: 20px;
  height: 20px;
  background-color: white;
  font-size: 20px;
  &:not([disabled]):hover {
    cursor: pointer;
  }
`;

const NoteModal = styled(Card)<PositionProps>`
  ${position}
`;

const ButtonsWrapper = styled(Box)<PositionProps>`
  ${position}
`;

const TextArea = styled.textarea<{
  maxLength: number;
  placeholder: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  disabled: boolean;
  children?: React.ReactNode;
}>`
  box-sizing: border-box;
  width: 100%;
  height: 174px;
  border-color: #f4f4f4;
  background-color: #fbfbfb;
  padding: 24px;
  color: #64798c;
  font-size: 14px;

  &::placeholder {
    color: #7e9ab3;
    opacity: 0.3;
  }
`;

const DeleteButton = styled.button<{
  role: string;
  children: React.ReactNode;
  onClick: () => void;
  onMouseOver: () => void;
  onMouseOut: () => void;
}>`
  width: 48px;
  height: 48px;
  border: none;
  background-color: transparent;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 24px 0 rgba(222, 222, 222, 0.5);
  &:not([disabled]):hover {
    cursor: pointer;
  }
`;

const NoteCard: React.FunctionComponent<NoteCardProps> = ({
  isPostSaleVDP,
  notes,
  previousNotes,
  vrm,
  setPreviousNotes,
  setNotes,
  setShowOverlay,
  setShowNotesBox,
  setNoteButton,
  setShowDiscardBox,
  setShowDeleteBox,
  setNotesAnimationActive,
  setHasNotesError,
  setNoteOpen,
  lotNumber,
  saleCode,
  inventoryId,
  isToUseAuthZero = false,
  accessToken,
}) => {
  const [isMouseOver, setIsMouseOver] = useState(false);

  const handleCloseButton = () => {
    if (notes !== previousNotes) {
      setShowNotesBox(false);
      setShowDiscardBox(true);
      return;
    }

    setShowOverlay(false);
    setShowNotesBox(false);
    setNoteButton(previousNotes ? ACTIVE_BUTTON : DEFAULT_BUTTON);
    setNoteOpen(false);
  };

  const handleDoneButton = async () => {
    setShowNotesBox(false);
    setNotesAnimationActive(true);
    setShowOverlay(false);
    setNoteButton(ACTIVE_BUTTON);
    setNoteOpen(false);

    const result = await addNote(
      vrm,
      notes,
      saleCode,
      inventoryId,
      isToUseAuthZero,
      accessToken
    );
    if (result.status > 200 || result?.degradedServices?.length > 0) {
      setHasNotesError(true);
      setShowNotesBox(false);
      setPreviousNotes(null);
      setNoteButton(DEFAULT_BUTTON);
      return;
    }

    setPreviousNotes(notes);
    setHasNotesError(false);
    setNoteButton(ACTIVE_BUTTON);
    return;
  };

  const handleDeleteButton = () => {
    setShowNotesBox(false);
    setShowDeleteBox(true);
  };

  return (
    <NoteModal
      position="absolute"
      zIndex={[10, 4]}
      width={{ mobile: "240px", tablet: "346px", desktop: "368px" }}
      height="auto"
      marginX={0}
      marginY={0}
      top={[lotNumber.length > 7 ? 80 : 48, 55]}
      right={[30, 30, 30, 14]}
    >
      <Box display="flex" justifyContent="space-between" mb={14}>
        <Typography m={0} variant="h6">
          Notes
        </Typography>
        <CloseButton onClick={handleCloseButton}>&times;</CloseButton>
      </Box>
      <TextArea
        maxLength={250}
        placeholder="Start typing your note here..."
        value={notes || ""}
        onChange={(evt) => {
          setNotes(evt.target.value);
        }}
        disabled={isPostSaleVDP}
      ></TextArea>
      {!isPostSaleVDP && (
        <Box display="flex" justifyContent="space-between" mt={10}>
          <Button
            variant="primary"
            text="Done"
            onClick={handleDoneButton}
            disabled={!notes}
            css="flex: 1; &:not([disabled]):hover {
            cursor: pointer;
          }"
            mr={previousNotes ? 12 : 0}
            data-en="click"
            data-ec="button"
            data-el="note"
            data-ea={previousNotes ? "edit" : "add"}
            data-es=""
          ></Button>
          {previousNotes && (
            <DeleteButton
              role="delete"
              onClick={handleDeleteButton}
              onMouseOver={() => setIsMouseOver(true)}
              onMouseOut={() => setIsMouseOver(false)}
              data-en="click"
              data-ec="button"
              data-el="note"
              data-ea="delete"
              data-es=""
            >
              {isMouseOver && <BinSolidSVG color="#7e9ab3" />}
              {!isMouseOver && <BinSVG color="#7e9ab3" />}
            </DeleteButton>
          )}
        </Box>
      )}
    </NoteModal>
  );
};

const DeleteModal: React.FunctionComponent<DeleteModalProps> = ({
  setShowOverlay,
  setPreviousNotes,
  setShowNotesBox,
  setNotes,
  setNoteButton,
  setShowDeleteBox,
  setHasDeleteNotesError,
  notes,
  setNoteOpen,
  vrm,
  saleCode,
  inventoryId,
  isToUseAuthZero = false,
  accessToken,
}) => {
  const handleDelete = async () => {
    setNoteOpen(false);
    setShowOverlay(false);
    setShowDeleteBox(false);

    const result = await deleteNote(
      vrm,
      saleCode,
      inventoryId,
      isToUseAuthZero,
      accessToken
    );

    if (result.status > 200 || result?.degradedServices?.length > 0) {
      setShowOverlay(false);
      setShowDeleteBox(false);
      setNoteButton(ACTIVE_BUTTON);
      setPreviousNotes(notes);
      setHasDeleteNotesError(true);
      return;
    }

    setNotes(null);
    setPreviousNotes(null);
    setNoteButton(DEFAULT_BUTTON);
    return;
  };

  const handleCancel = () => {
    setShowDeleteBox(false);
    setShowNotesBox(true);
  };

  return (
    <NoteModal
      position="absolute"
      zIndex={4}
      width={{ mobile: "240px", tablet: "368px", desktop: "368px" }}
      height={186}
      marginX={0}
      marginY={0}
      top={{ mobile: "48px", tablet: "55px", desktop: "55px" }}
      bottom={["auto", -108, -110]}
      right={[30, 30, 14]}
    >
      <Typography m={0} textAlign="center" variant="h6">
        Confirm delete
      </Typography>
      <Typography
        variant="caption"
        textAlign="center"
        color="grey-blue"
        mb={22}
      >
        Are you sure you want to delete this note? This cannot be undone.
      </Typography>
      <Box display="flex" justifyContent="space-between">
        <Button
          variant="primary"
          onClick={handleDelete}
          width={{ mobile: "40%", tablet: "152px", desktop: "152px" }}
          px={24}
          css="&:not([disabled]):hover {
            cursor: pointer;
          }"
          data-en="click"
          data-ec="button"
          data-el="note"
          data-ea="delete_confirm"
        >
          Delete note
        </Button>
        <Button
          variant="secondary"
          onClick={handleCancel}
          width={{ mobile: "40%", tablet: "152px", desktop: "152px" }}
          css="&:not([disabled]):hover {
            cursor: pointer;
          }"
        >
          Cancel
        </Button>
      </Box>
    </NoteModal>
  );
};

const DiscardModal: React.FunctionComponent<DiscardModalProps> = ({
  setShowOverlay,
  setShowDiscardBox,
  setShowNotesBox,
  setNotes,
  previousNotes,
  setNoteButton,
}) => {
  const handleDiscard = () => {
    setShowOverlay(false);
    setShowDiscardBox(false);
    setNotes(previousNotes);
    setNoteButton(previousNotes ? ACTIVE_BUTTON : DEFAULT_BUTTON);
  };
  const handleCancel = () => {
    setShowDiscardBox(false);
    setShowNotesBox(true);
  };

  return (
    <NoteModal
      position="absolute"
      zIndex={4}
      width={368}
      height={186}
      marginX={0}
      marginY={0}
      top={[50, "auto"]}
      bottom={["auto", -200]}
      right={[30, 30, 30, 14]}
    >
      <Typography m={0} textAlign="center" variant="h6">
        Discard changes
      </Typography>
      <Typography
        variant="caption"
        textAlign="center"
        color="grey-blue"
        mb={22}
      >
        Are you sure you want to discard the changes you’ve made to this note?
      </Typography>
      <Box display="flex" justifyContent="space-between">
        <Button
          variant="primary"
          onClick={handleDiscard}
          width={152}
          pl={22}
          pr={22}
          css="&:not([disabled]):hover {
            cursor: pointer;
          }"
        >
          Discard changes
        </Button>
        <Button
          variant="secondary"
          onClick={handleCancel}
          width={152}
          css="&:not([disabled]):hover {
            cursor: pointer;
          }"
        >
          Cancel
        </Button>
      </Box>
    </NoteModal>
  );
};

const ErrorBuyerInteraction: React.FunctionComponent<
  ErrorBuyerInteractionProps
> = ({ hasNotesError, closeBanner, hasDeleteNotesError, hasFeatureError }) => {
  const errorMessageNotes = hasDeleteNotesError
    ? "We're sorry, a problem occurred when deleting your note. Please try again."
    : "We're sorry, a problem occurred when saving your note. We've retained it for now, but please try again.";

  const errorMessage =
    hasNotesError || hasDeleteNotesError
      ? errorMessageNotes
      : hasFeatureError
      ? "We're sorry, a problem occurred when featuring this vehicle, please try again."
      : "We're sorry, a problem occurred when tracking this vehicle, please try again.";

  return (
    <Banner
      variant="warning"
      title="Service currently unavailable"
      primaryLinkLabel="Close"
      primaryLinkAction={closeBanner}
      paddingX={[20, 100, 20]}
      paddingY={20}
    >
      <Typography margin={0} variant="body" color="grey-blue">
        {errorMessage}
      </Typography>
    </Banner>
  );
};

const VehicleInteraction: React.FunctionComponent<VehicleInteractionProps> = ({
  isOnSale,
  vrm,
  lotNumber,
  saleCode,
  description,
  lotNote,
  isTracked,
  setIsTracked,
  printSections,
  isProvisional,
  setIsFeatured,
  isFeatured,
  isAdmin,
  isFeatureVehicle,
  shouldRestrict,
  inventoryId,
  isToUseAuthZero = false,
  accessToken,
}) => {
  const {
    source: { urlSource },
  }: StoreType = useSelector(GET_SOURCE_STATE, shallowEqual);

  const isPostSaleVDP = urlSource === "Purchases" || urlSource === "Invoices";

  const showBox = !isProvisional;

  const [showDeleteBox, setShowDeleteBox] = useState(false);
  const [showDiscardBox, setShowDiscardBox] = useState(false);
  const [trackAnimationActive, setTrackAnimationActive] = useState(false);
  const [featureAnimationActive, setFeatureAnimationActive] = useState(false);
  const [notesAnimationActive, setNotesAnimationActive] = useState(false);
  const [notes, setNotes] = useState(lotNote);
  const [previousNotes, setPreviousNotes] = useState(lotNote);
  const [trackButtonText, setTrackButtonText] = useState("");
  const [featuredButtonText, setFeaturedButtonText] = useState("");

  const [showNotesBox, setShowNotesBox] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [computeAndSetOffset, windowOffsetRef] = useWindowOffset();

  const trackButtonWidth = isTracked ? 134 : 115;
  const featureButtonWidth = isFeatured ? 134 : 115;
  const noteButtonWidth = !showOverlay && !!previousNotes ? 165 : 134;
  const showNoteButtonText = getNoteButtonDisplayCondition();
  const [hasTrackError, setHasTrackError] = useState(false);
  const [hasFeatureError, setHasFeatureError] = useState(false);
  const [hasNotesError, setHasNotesError] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [hasDeleteNotesError, setHasDeleteNotesError] = useState(false);
  const [noteButton, setNoteButton] = useState(
    lotNote ? ACTIVE_BUTTON : DEFAULT_BUTTON
  );

  const notesButtonIsDisabled = isPostSaleVDP && !Boolean(notes);

  const handleUntrackVehicle = async () => {
    setTrackAnimationActive(true);
    const result = await untrackVehicle(
      vrm,
      saleCode,
      inventoryId,
      isToUseAuthZero,
      accessToken
    );

    if (result.status > 200 || result?.degradedServices?.length > 0) {
      setIsTracked(true);
      setHasTrackError(true);

      return;
    }

    setIsTracked(false);
    setHasTrackError(false);

    return;
  };

  const handleUnfeatureVehicle = async () => {
    setFeatureAnimationActive(true);

    const result = await unfeatureVehicle(vrm, isToUseAuthZero, accessToken);

    if (result.status > 204 || result?.degradedServices?.length > 0) {
      setIsFeatured(true);
      setHasFeatureError(true);

      return;
    }

    setIsFeatured(false);
    setHasFeatureError(false);

    return;
  };

  const handleTrackVehicle = async () => {
    const result = await trackVehicle(
      vrm,
      inventoryId,
      isToUseAuthZero,
      accessToken
    );

    if (result.status > 200 || result?.degradedServices?.length > 0) {
      setHasTrackError(true);
      setIsTracked(false);
      return;
    }

    setTrackAnimationActive(true);
    setIsTracked(true);
    setHasTrackError(false);
    return;
  };

  const handleFeatureVehicle = async () => {
    const result = await featureVehicle(vrm, isToUseAuthZero, accessToken);

    if (result.status > 204 || result?.degradedServices?.length > 0) {
      setHasFeatureError(true);
      setIsFeatured(false);
      return;
    }

    setTrackAnimationActive(true);
    setIsFeatured(true);
    setHasFeatureError(false);
    return;
  };

  const handleNoteButton = () => {
    setNoteButton({
      bgColor: "primary-white",
      color: "black",
      iconColor: "primary-black",
    });
    setShowNotesBox(true);
    setShowOverlay(true);
    setNotesAnimationActive(true);
    setNoteOpen(true);
  };

  const closeBanner = useCallback(() => {
    setHasNotesError(false);
    setHasTrackError(false);
    setHasDeleteNotesError(false);
  }, []);

  useEffect(() => {
    const notesAnimationTimeout = setTimeout(
      () => setNotesAnimationActive(false),
      500
    );
    return () => {
      clearTimeout(notesAnimationTimeout);
    };
  }, [notesAnimationActive]);

  useEffect(() => {
    const trackAnimationTimeout = setTimeout(
      () => setTrackAnimationActive(false),
      500
    );
    return () => {
      clearTimeout(trackAnimationTimeout);
    };
  }, [trackAnimationActive]);

  useEffect(() => {
    const featureAnimationTimeout = setTimeout(
      () => setFeatureAnimationActive(false),
      500
    );
    return () => {
      clearTimeout(featureAnimationTimeout);
    };
  }, [featureAnimationActive]);

  useEffect(() => {
    computeAndSetOffset();
  }, [computeAndSetOffset]);

  useEffect(() => {
    const handleResize = () => {
      const trackedText = getTrackButtonText(isTracked);
      const featuredText = getFeaturedButtonText(isFeatured);

      setTrackButtonText(trackedText);
      setFeaturedButtonText(featuredText);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  useEffect(() => {
    const text = getTrackButtonText(isTracked);
    setTrackButtonText(text);
  }, [isTracked]);

  useEffect(() => {
    const text = getFeaturedButtonText(isFeatured);

    setFeaturedButtonText(text);
  }, [isFeatured]);

  return (
    <>
      {(hasTrackError ||
        hasNotesError ||
        hasFeatureError ||
        hasDeleteNotesError) && (
        <ErrorBuyerInteraction
          hasNotesError={hasNotesError}
          hasDeleteNotesError={hasDeleteNotesError}
          closeBanner={closeBanner}
          hasFeatureError={hasFeatureError}
        />
      )}
      {showOverlay && <Overlay />}
      <ResponsiveLayout>
        <VehicleDetailsWrapper
          position="relative"
          top={[-69, -84, -100]}
          zIndex={3}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          maxWidth="100%"
          width="100%"
          marginRight="0"
        >
          {showOverlay && <TextOverlay />}
          {showDeleteBox && (
            <DeleteModal
              setShowOverlay={setShowOverlay}
              vrm={vrm}
              saleCode={saleCode}
              setPreviousNotes={setPreviousNotes}
              setShowNotesBox={setShowNotesBox}
              setNotes={setNotes}
              setNoteButton={setNoteButton}
              setShowDeleteBox={setShowDeleteBox}
              setHasDeleteNotesError={setHasDeleteNotesError}
              notes={notes}
              setNoteOpen={setNoteOpen}
              inventoryId={inventoryId}
              isToUseAuthZero={isToUseAuthZero}
              accessToken={accessToken}
            />
          )}
          {showDiscardBox && (
            <DiscardModal
              previousNotes={previousNotes}
              setShowOverlay={setShowOverlay}
              setShowDiscardBox={setShowDiscardBox}
              setShowNotesBox={setShowNotesBox}
              setNotes={setNotes}
              setNoteButton={setNoteButton}
            />
          )}
          {showNotesBox && (
            <NoteCard
              isPostSaleVDP={isPostSaleVDP}
              notes={notes}
              setNotes={setNotes}
              previousNotes={previousNotes}
              setPreviousNotes={setPreviousNotes}
              setShowOverlay={setShowOverlay}
              setShowNotesBox={setShowNotesBox}
              setNoteButton={setNoteButton}
              setShowDiscardBox={setShowDiscardBox}
              setShowDeleteBox={setShowDeleteBox}
              setNotesAnimationActive={setNotesAnimationActive}
              vrm={vrm}
              saleCode={saleCode}
              setHasNotesError={setHasNotesError}
              setNoteOpen={setNoteOpen}
              lotNumber={lotNumber}
              inventoryId={inventoryId}
              isToUseAuthZero={isToUseAuthZero}
              accessToken={accessToken}
            />
          )}
          <Box display="flex" flexDirection="row">
            <Box
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
              flexGrow={1}
              width={["50%", "66%"]}
              height="auto"
            >
              <Vrm vrm={vrm} />
              {lotNumber && lotNumber !== "0" && (
                <LotNumber data-testid="lot-number" lotNumber={lotNumber} />
              )}
            </Box>
            <ButtonsWrapper
              display="flex"
              alignItems="flex-end"
              justifyContent="flex-end"
              flexGrow={2}
              pr={0}
            >
              {(isAdmin || isFeatureVehicle) && !isPostSaleVDP && (
                <InteractionButton
                  variant="secondary-white"
                  mr={12}
                  pl={[2, 2, 2, 24]}
                  pr={[0, 0, 0, 24]}
                  width={[
                    40,
                    featureButtonWidth,
                    featureButtonWidth,
                    featureButtonWidth,
                  ]}
                  height={[40, 48, 48, 48]}
                  text={featuredButtonText}
                  aria-label={isFeatured ? "Featured" : "Feature"}
                  icon={isFeatured ? "star-solid" : "star-light"}
                  onClick={
                    isFeatured ? handleUnfeatureVehicle : handleFeatureVehicle
                  }
                  isAnimationActive={featureAnimationActive}
                  css={`
                    ${css({
                      borderColor: isFeatured
                        ? "secondary-orange"
                        : "primary-white",
                      svg: {
                        path: {
                          fill: isFeatured
                            ? "secondary-orange"
                            : "primary-white",
                        },
                      },
                    })}
                  `}
                  data-en="click"
                  data-ec="button"
                  data-el="vehicleFeature"
                  data-ea={isFeatured ? "unfeature" : "feature"}
                  data-es=""
                />
              )}
              {showBox && isOnSale && !isPostSaleVDP && !shouldRestrict && (
                <InteractionButton
                  variant="secondary-white"
                  mr={12}
                  pl={[2, 2, 2, 24]}
                  pr={[0, 0, 0, 24]}
                  width={[
                    40,
                    trackButtonWidth,
                    trackButtonWidth,
                    trackButtonWidth,
                  ]}
                  height={[40, 48, 48, 48]}
                  text={trackButtonText}
                  aria-label={isTracked ? "Tracked" : "Track"}
                  icon={isTracked ? "star-solid" : "star-light"}
                  onClick={
                    isTracked ? handleUntrackVehicle : handleTrackVehicle
                  }
                  isAnimationActive={trackAnimationActive}
                  css={`
                    ${css({
                      borderColor: isTracked
                        ? "secondary-orange"
                        : "primary-white",
                      svg: {
                        path: {
                          fill: isTracked
                            ? "secondary-orange"
                            : "primary-white",
                        },
                      },
                    })}
                  `}
                  data-en="click"
                  data-ec="button"
                  data-el="vehicleTrack"
                  data-ea={isTracked ? "untrack" : "track"}
                  data-es=""
                />
              )}
              {(isOnSale || isPostSaleVDP) && !shouldRestrict && (
                <NotesButton
                  title={
                    notesButtonIsDisabled
                      ? "No notes available for this vehicle"
                      : undefined
                  }
                  variant="secondary-white"
                  mr={12}
                  pl={[2, 2, 2, 24]}
                  pr={[0, 0, 0, 24]}
                  color={noteButton.color}
                  backgroundColor={noteButton.bgColor}
                  onClick={handleNoteButton}
                  disabled={notesButtonIsDisabled}
                  isAnimationActive={notesAnimationActive}
                  width={[40, noteButtonWidth]}
                  height={[40, 48]}
                  aria-label={
                    !showOverlay && !!previousNotes ? "Note Added" : "Notes"
                  }
                  icon={
                    !showOverlay && !!previousNotes
                      ? "sticky-note-solid"
                      : "sticky-note-regular"
                  }
                  css={`
                    ${css({
                      borderColor:
                        !showOverlay && !!previousNotes
                          ? "secondary-orange"
                          : "primary-white",
                      svg: {
                        path: {
                          fill: noteButton.iconColor,
                        },
                      },
                    })}
                  `}
                  data-en="click"
                  data-ec="button"
                  data-el="note"
                  {...(noteOpen && { "data-ea": "open" })}
                >
                  {showNoteButtonText && (
                    <TextWrapper
                      showFullWidth={!showOverlay && !!previousNotes}
                    >
                      <span>
                        {!showOverlay && !!previousNotes
                          ? "Note Added"
                          : "Notes"}
                      </span>
                    </TextWrapper>
                  )}
                </NotesButton>
              )}
              {showBox && (isOnSale || isPostSaleVDP) && !shouldRestrict && (
                <PrintMenuWithList
                  printSections={printSections}
                  isToUseAuthZero={isToUseAuthZero}
                  accessToken={accessToken}
                />
              )}
            </ButtonsWrapper>
          </Box>
          <Typography
            variant="h1"
            color="white"
            mt={[3, 4]}
            mb={2}
            css={`
              ${css({
                fontSize: [28, 42],
              })}
            `}
          >
            {description}
          </Typography>
        </VehicleDetailsWrapper>
      </ResponsiveLayout>
    </>
  );
};

const getTrackButtonText = (isTracked) => {
  if (typeof window === "undefined") {
    return "";
  }

  if (window.innerWidth < 768) {
    return "";
  }

  return isTracked ? "Tracked" : "Track";
};

const getFeaturedButtonText = (isFeatured) => {
  if (typeof window === "undefined") {
    return "";
  }

  if (window.innerWidth < 768) {
    return "";
  }

  return isFeatured ? "Featured" : "Feature";
};

const getNoteButtonDisplayCondition = () => {
  if (typeof window === "undefined") {
    return false;
  }

  if (window.innerWidth < 768) {
    return false;
  }

  return true;
};

export default VehicleInteraction;
